import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import type { DraftCalendarItem } from "@/types/draftCalendar";

export const DRAFT_CALENDAR_EXPORT_HEADERS = [
  "Sign Off Status", "Draft Meeting Status", "Summit Meeting Status", "DU Status", "Tier",
  "Prior ID", "New ID", "Business", "Meeting Name",
  "Room Block Start", "Room Block End", "Meeting Start", "Meeting End", "Start Time", "End Time",
  "NightGuestNumber", "All In Attendees", "Meeting Type", "Business Line", "Attendee Level",
  "Meeting Category", "CLO or Sponsoring PPMD", "Program Contact", "Business Advisor", "Program Manager",
];

function formatExportDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${days[d.getDay()]}, ${mm}-${dd}-${d.getFullYear()}`;
}

function itemToExportRow(item: DraftCalendarItem): (string | number)[] {
  return [
    item.signOffStatus ?? "",
    item.state ?? "",                  // Draft Meeting Status
    item.summitMeetingStatus ?? "",    // Summit Meeting Status (new)
    item.duMeetingStatus ?? "",        // DU Status (new)
    item.status ?? "",                 // Tier (Core / Secondary)
    item.priorId ?? "",
    item.meetingId ?? "",
    item.business ?? "",
    item.meetingName ?? "",
    formatExportDate(item.roomBlockStart),
    formatExportDate(item.roomBlockEnd),
    formatExportDate(item.eventStart),
    formatExportDate(item.eventEnd),
    item.startTimeString ?? "",
    item.endTimeString ?? "",
    item.nightGuestNumber ?? "",
    item.allInAttendees ?? "",
    item.meetingType ?? "",
    item.businessLine ?? "",
    item.attendeeLevel ?? "",
    item.meetingCategory ?? "",
    item.summitProgramOwnerName ?? "",
    item.summitProgramContact ?? "",
    item.summitMeetingPlanner ?? "",
    item.programManager ?? "",
  ];
}

export function exportDraftCalendarToExcel(items: DraftCalendarItem[]): void {
  const wb = XLSX.utils.book_new();
  const aoa: (string | number)[][] = [DRAFT_CALENDAR_EXPORT_HEADERS];

  if (items.length === 0) {
    aoa.push(["No record found.", ...Array<string>(DRAFT_CALENDAR_EXPORT_HEADERS.length - 1).fill("")]);
  } else {
    items.forEach((item) => aoa.push(itemToExportRow(item)));
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Green header styling — RGB(38, 137, 13)
  const headerStyle = {
    fill: { patternType: "solid" as const, fgColor: { rgb: "26890D" } },
    font: { color: { rgb: "FFFFFF" }, bold: true },
    border: {
      top: { style: "thin" as const, color: { rgb: "000000" } },
      bottom: { style: "thin" as const, color: { rgb: "000000" } },
      left: { style: "thin" as const, color: { rgb: "000000" } },
      right: { style: "thin" as const, color: { rgb: "000000" } },
    },
    alignment: { horizontal: "left" as const },
  };

  for (let c = 0; c < DRAFT_CALENDAR_EXPORT_HEADERS.length; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[ref]) ws[ref].s = headerStyle;
  }

  ws["!cols"] = DRAFT_CALENDAR_EXPORT_HEADERS.map((h) => ({ wch: Math.max(h.length + 2, 18) }));

  XLSX.utils.book_append_sheet(wb, ws, "Draft Calendar");
  XLSX.writeFile(wb, "DraftCalendar.xlsx");
  toast.success("Export successful");
}
